import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMount } from 'react-use';
import { Column } from '@zeroedin-react/zi-common/lib/';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faEllipsisV, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import {
	AlertVariant,
	Button,
	Dropdown,
	DropdownItem,
	DropdownToggle,
	Label,
	Modal,
	ModalVariant,
} from '@patternfly/react-core';
import { SubheaderContext } from '../layout/Layout';
import PageTitleSubheader from '../layout/subheader/PageTitleSubheader';
import FilterTableLayout from '../layout/FilterTableLayout';
import { TZone, Zone } from '../api/zone/Zone';
import { Subscription, TSubscription } from '../api/subscriptions/Subscription';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { capitalize, unixToHumanTimestamp } from '../helper/Helpers';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { Subscriber, TSubscriber } from '../api/subscriptions/Subscriber';
import DualListSelectorGeneric from '../components/helpers/DualListSelectorGeneric';
import Loader from '../components/utils/Loader';
import { DummyPermission, useIsGranted } from '../helper/PermissionOverride';
import ZiTable, { Action } from '@zeroedin-react/zi-common/lib/components/table/ZiTable';

export default function Subscriptions(): ReactElement {
	const { addToast } = useToast();
	const [setSubheader]: SubheaderContext = useOutletContext();
	const [tableData, setTableData] = useState<TSubscription[]>([]);
	const [tableLoading, setTableLoading] = useState<boolean>(true);
	const [subscriberTableLoading, setSubscriberTableLoading] = useState<boolean>(true);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isSubZonesModalOpen, setIsSubZonesModalOpen] = useState<boolean>(false);
	const [activeSub, setActiveSub] = useState<TSubscription>(
		Subscription.Default() as TSubscription
	);
	const [zones, setZones] = useState<TZone[]>([]);
	const [subZones, setSubZones] = useState<TSubscriber[]>([]);
	const onToggle = (id: string, isOpen: boolean) => {
		setOpenDropdownId(isOpen ? id : '');
	};

	const [commonZones, setCommonZones] = React.useState<React.ReactNode[]>([]);
	const [chosenSubscriptionZones, setChosenSubscriptionZones] = React.useState<React.ReactNode[]>(
		[]
	);
	const chosenPaneRef = useRef<HTMLDivElement>(null);
	const [openDropdownId, setOpenDropdownId] = useState('');
	const [shouldReloadSubscriptions, setShouldReloadSubscriptions] = useState<boolean>(false);

	const selectedColumns: Column<TSubscription>[] = [
		{
			title: 'Subscription Name',
			columnName: 'name',
			sortable: true,
		},
		{
			title: 'Version #',
			columnName: 'version',
			sortable: true,
		},
		{
			title: 'Status',
			columnName: 'status',
			customAccessor: (item) => {
				return capitalize(item.status);
			},
			sortable: true,
		},
		{
			title: '# of Subscribers',
			columnName: 'subscribers',
			customAccessor: (item) => {
				const zoneIdsFromZoneObjects = Array.from(
					new Set(
						item.subscribers
							.map((x) => x.zone as TZone)
							.filter((x) => x.id !== undefined)
							.flatMap((x) => x.id)
					)
				);
				const zoneIdsFromIds = item.subscribers
					.filter((x) => typeof x.zone === 'number')
					.map((x) => x.zone as number);

				const zoneIdsForSub = [...zoneIdsFromZoneObjects, ...zoneIdsFromIds];
				return zoneIdsForSub.length;
			},
		},
	];

	const actions: Action<TSubscription, typeof DummyPermission>[] = [
		{
			name: (
				<>
					Manage Subscribers <FontAwesomeIcon icon={faEdit} />
				</>
			),
			callback: (item) => {
				setActiveSub(item);
				getAllSubscribers(item.id);
				setIsModalOpen(true);
			},
			permission: DummyPermission.EDIT,
		},
	];
	useMount(() => {
		setSubheader(
			<PageTitleSubheader
				pageTitle="Subscriptions"
				pageDescription="Manage your Subscriptions."
			/>
		);

		loadPageData();
	});

	useEffect(() => {
		if (!isSubZonesModalOpen) {
			setChosenSubscriptionZones([]);
		}

		const filteredZones =
			subZones.length > 0
				? zones.filter((x) => !subZones.some((y) => (y.zone as TZone).id == x.id))
				: zones;
		const mappedCommonZones: React.ReactNode[] = filteredZones.map((option, index) => (
			<div
				key={`option${index + 1}`}
				id={option.id.toString()}
				data-id={option.id}
			>
				{option.name}
			</div>
		));

		setCommonZones(mappedCommonZones);
	}, [isSubZonesModalOpen, subZones]);

	useEffect(() => {
		if (!isModalOpen && shouldReloadSubscriptions) {
			loadPageData();
			setShouldReloadSubscriptions(false);
		}
	}, [isModalOpen]);

	const loadPageData = () => {
		setTableLoading(true);

		void Subscription.GetAll(['subscribers']).then((response) => {
			setTableData(response);
			setTableLoading(false);
		});

		void Zone.GetAll().then((response) => {
			setZones(response);

			const mappedCommonZones: React.ReactNode[] = response.map((option, index) => (
				<div
					key={`option${index + 1}`}
					id={option.id.toString()}
					data-id={option.id}
				>
					{option.name}
				</div>
			));

			setCommonZones(mappedCommonZones);
		});
	};

	const getAllSubscribers = (subscriptionId: string) => {
		setSubscriberTableLoading(true);

		void Subscriber.GetAll(['zone']).then((response) => {
			const subscribers = response.filter((x) => x.subscription == subscriptionId);
			setSubZones(subscribers);
			setSubscriberTableLoading(false);
		});
	};

	const handleClose = () => {
		setIsModalOpen(false);
		setSubZones([]);
	};

	const entityTable = (
		<ZiTable<TSubscription, typeof DummyPermission>
			ariaLabel={'Subscriptions'}
			columns={selectedColumns}
			data={tableData}
			caption="Subscriptions"
			actions={actions}
			loading={tableLoading}
			useIsGranted={useIsGranted}
			Permission={DummyPermission}
		/>
	);

	const applyUpgradeSubscriber = (subscriberId: string) => {
		Subscriber.ApplyOrUpgrade(subscriberId)
			.then((response) => {
				addToast(response.message, AlertVariant.success);
			})
			.catch(() => {
				addToast(
					'An error occured while applying subscription updates.',
					AlertVariant.danger
				);
			});
	};

	const ellipsisDropdownTemplate = (id: string, version: number, status: string) => (
		<Dropdown
			key={id}
			isOpen={openDropdownId === id}
			toggle={
				<DropdownToggle
					onToggle={(isOpen) => onToggle(id, isOpen)}
					toggleIndicator={null}
					aria-label="Actions"
				>
					<FontAwesomeIcon icon={faEllipsisV} />
				</DropdownToggle>
			}
			dropdownItems={[
				<DropdownItem
					key="1"
					onClick={() => {
						applyUpgradeSubscriber(id);
					}}
				>
					{version == 1 || version == 2 ? 'Apply' : 'Upgrade'} Subscription
				</DropdownItem>,
				<DropdownItem
					className={`${status == 'draft' ? 'hidden' : ''}`}
					key="2"
				>
					Revoke Subscription
				</DropdownItem>,
			]}
			isPlain
			position="right"
		/>
	);

	const entityChildTable = (
		<Table
			aria-label="Entity Child Table"
			variant="compact"
			className="tblZoneSubscription"
		>
			<Thead
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
			>
				<Tr
					onPointerEnterCapture={undefined}
					onPointerLeaveCapture={undefined}
				>
					<Th
						onPointerEnterCapture={undefined}
						onPointerLeaveCapture={undefined}
					>
						Zone Name
					</Th>
					<Th
						onPointerEnterCapture={undefined}
						onPointerLeaveCapture={undefined}
					>
						Version Applied
					</Th>
					<Th
						onPointerEnterCapture={undefined}
						onPointerLeaveCapture={undefined}
					>
						Date Version Last Applied
					</Th>
					<Th
						onPointerEnterCapture={undefined}
						onPointerLeaveCapture={undefined}
					>
						Status
					</Th>
					<Th
						onPointerEnterCapture={undefined}
						onPointerLeaveCapture={undefined}
					>
						Actions
					</Th>
				</Tr>
			</Thead>
			<Tbody
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
			>
				{subscriberTableLoading && (
					<>
						<Tr
							onPointerEnterCapture={undefined}
							onPointerLeaveCapture={undefined}
						>
							<Td
								onPointerEnterCapture={undefined}
								onPointerLeaveCapture={undefined}
								colSpan={5}
							></Td>
						</Tr>
						<Tr
							onPointerEnterCapture={undefined}
							onPointerLeaveCapture={undefined}
						>
							<Td
								onPointerEnterCapture={undefined}
								onPointerLeaveCapture={undefined}
								colSpan={5}
							>
								<Loader />
							</Td>
						</Tr>
					</>
				)}
				{subZones.length == 0 && !subscriberTableLoading && (
					<>
						<br />
						<Tr
							onPointerEnterCapture={undefined}
							onPointerLeaveCapture={undefined}
						>
							<Td
								className="text-center"
								colSpan={5}
								onPointerEnterCapture={undefined}
								onPointerLeaveCapture={undefined}
							>
								No subscribers were found
							</Td>
						</Tr>
					</>
				)}
				{!subscriberTableLoading &&
					subZones.map((x) => {
						const currentZone = zones.find((y) => y.id == (x.zone as TZone).id);
						const currentSub = tableData.find((y) => y.id == x.subscription);
						return (
							<Tr
								onPointerEnterCapture={undefined}
								onPointerLeaveCapture={undefined}
							>
								<Td
									onPointerEnterCapture={undefined}
									onPointerLeaveCapture={undefined}
								>
									{currentZone?.name} {`(${currentZone?.prefix ?? ''})`}
								</Td>
								<Td
									onPointerEnterCapture={undefined}
									onPointerLeaveCapture={undefined}
								>
									{currentSub?.version}
								</Td>
								<Td
									onPointerEnterCapture={undefined}
									onPointerLeaveCapture={undefined}
								>
									{unixToHumanTimestamp(x?.subscribed_at)}
								</Td>
								<Td
									onPointerEnterCapture={undefined}
									onPointerLeaveCapture={undefined}
								>
									{x?.status}
								</Td>
								<Td
									onPointerEnterCapture={undefined}
									onPointerLeaveCapture={undefined}
								>
									{ellipsisDropdownTemplate(
										x.id,
										currentSub?.version ?? 1,
										x.status
									)}
								</Td>
							</Tr>
						);
					})}
			</Tbody>
		</Table>
	);

	const saveSubsriberZones = () => {
		const promises: Promise<TSubscriber>[] = [];

		if (chosenPaneRef.current) {
			const elements = chosenPaneRef.current.querySelectorAll('[data-id]');
			elements.forEach((element) => {
				const id = element.getAttribute('data-id');
				if (id) {
					const createSubscriber = Subscriber.New({
						status: 'active',
						subscription: activeSub.id,
						zone: +id,
					});
					promises.push(createSubscriber);
				}
			});
		}

		Promise.all(promises)
			.then((response) => {
				setIsSubZonesModalOpen(false);
				getAllSubscribers(activeSub.id);
				setShouldReloadSubscriptions(true);
			})
			.catch((error) => {
				console.log('error', error);
			});
	};

	return (
		<React.Fragment>
			<Modal
				variant={ModalVariant.large}
				title="Subscription Management"
				isOpen={isModalOpen}
				onClose={handleClose}
				className="modal-min-height-500"
			>
				<hr />
				<br />
				<label>
					Subscription:
					<Label
						id="subscription"
						className="lblCurrentSub"
					>
						<b>{activeSub.name}</b>
					</Label>
				</label>
				<div className="pull-right">
					<Button
						data-testid={'add-subscriber-button'}
						variant={'primary'}
						onClick={() => {
							setIsSubZonesModalOpen(true);
						}}
					>
						Add Zone as new Subscriber
					</Button>
				</div>
				<br />
				<br />
				{entityChildTable}
			</Modal>
			<React.Fragment>
				<Button
					data-testid={'refresh-subscriber-button'}
					variant={'primary'}
					className="pull-right"
					style={{ right: '110px' }}
					onClick={() => {
						loadPageData();
					}}
				>
					Refresh <FontAwesomeIcon icon={faRefresh} />
				</Button>
				<FilterTableLayout table={entityTable} />
			</React.Fragment>
			<Modal
				variant={ModalVariant.large}
				title="Subscriber Management"
				isOpen={isSubZonesModalOpen}
				onClose={() => {
					setIsSubZonesModalOpen(false);
				}}
				className="modal-min-height-500"
			>
				<hr />
				<br />
				<label>
					Subscription:
					<Label
						id="subscription"
						className="lblCurrentSub"
					>
						<b>{activeSub.name}</b>
					</Label>
				</label>
				<br />
				<DualListSelectorGeneric
					leftHeading="Avaliable Zones"
					rightHeading="Chosen Zones"
					leftItems={commonZones}
					setLeftItems={setCommonZones}
					chosenOptions={chosenSubscriptionZones}
					setChosenOptions={setChosenSubscriptionZones}
					chosenPaneRef={chosenPaneRef}
				/>
				<br />
				<Button
					data-testid={'save-subscriber-button'}
					variant={'primary'}
					className="pull-right"
					onClick={() => {
						saveSubsriberZones();
					}}
				>
					Save
				</Button>
			</Modal>
		</React.Fragment>
	);
}
